import React from 'react';
import { connect } from 'react-redux';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SocialAuthButtons from '../AuthenticationPage/SocialAuthButtons/SocialAuthButtons';

import {
  IconCheckDisc,
  IconCoins,
  IconHanger,
  IconHeart,
  IconMagnifier,
  IconTruck,
} from './BliMedlemIcons';
import css from './BliMedlemPage.module.css';

// Guro's campaign photographs (AI-made, no text), as AVIF at their own size.
// Step 1 is the homepage on a phone (390px, signed out, as from 14 October);
// step 3 is her packing photo of 9 October.
const IMAGES = {
  hero: '/static/images/bli-medlem/hero-kvinne-ved-klesstativ.avif',
  about: '/static/images/bli-medlem/klaer-stablet-pa-seng.avif',
  browse: '/static/images/bli-medlem/steg-1-kvinne-ved-kommode.avif',
  steps: [
    '/static/images/bli-medlem/steg-1-forsiden-pa-mobil.avif',
    '/static/images/bli-medlem/steg-2-kvinne-ved-klesstativ.avif',
    '/static/images/bli-medlem/steg-3-pakke-klar-til-sending.avif',
  ],
};

const ABOUT_POINTS = [1, 2, 3];
const WHY_POINTS = [
  { n: 1, Icon: IconHanger },
  { n: 2, Icon: IconMagnifier },
  { n: 3, Icon: IconTruck },
  { n: 4, Icon: IconHeart },
];
const STEPS = [1, 2, 3];
// Only what the page does not already answer (Guro, 9 October): the price is
// in "Hva koster det å selge?", and needing to sell is "Du trenger ikke
// bestemme deg nå".
const FAQ_ITEMS = [1, 2, 3];

/**
 * "Opprett gratis konto": Vipps, Facebook and email as three pills. No
 * Google: the campaign's visitors arrive in the Facebook and Instagram in-app
 * browsers, where Google refuses to sign anyone in.
 */
const SignupBand = ({ headingId }) => {
  const routeConfiguration = useRouteConfiguration();

  const startIdp = idp => () => {
    const queryParams = new URLSearchParams({
      defaultReturn: pathByRouteName('LandingPage', routeConfiguration),
      defaultConfirm: pathByRouteName('ConfirmPage', routeConfiguration),
    });
    window.location.href = `${apiBaseUrl()}/api/auth/${idp}?${queryParams.toString()}`;
  };

  return (
    <section className={css.signupBand} aria-labelledby={headingId}>
      <h2 id={headingId} className={css.signupHeading}>
        <FormattedMessage id="BliMedlemPage.signupHeading" />
      </h2>
      <div className={css.signupOptions}>
        <SocialAuthButtons
          pill
          vippsLabelId="BliMedlemPage.signupVipps"
          facebookLabelId="BliMedlemPage.signupFacebook"
          googleLabelId="WelcomeForm.continueWithGoogle"
          showVippsLogin={!!process.env.REACT_APP_VIPPS_CLIENT_ID}
          showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
          showGoogleLogin={false}
          onAuthWithVipps={startIdp('vipps')}
          onAuthWithFacebook={startIdp('facebook')}
        >
          <NamedLink name="SignupPage" className={css.emailPill}>
            <svg
              className={css.emailIcon}
              width="22"
              height="22"
              viewBox="0 0 16 16"
              aria-hidden="true"
              style={{ fill: 'none' }}
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1.6" y="3.4" width="12.8" height="9.2" rx="1" />
              <path d="m2 4 6 4.6L14 4" />
            </svg>
            <FormattedMessage id="BliMedlemPage.signupEmail" />
          </NamedLink>
        </SocialAuthButtons>
      </div>
    </section>
  );
};

/**
 * "Bli medlem" (/bli-medlem): the landing page of the Facebook and Instagram
 * sign-up campaign, and a permanent way in from the footer.
 *
 * Guro's 9 October layout, built for sign-ups: the hero, "Opprett gratis
 * konto" right under it, who Zuprize is, why to create an account, a nudge
 * for those who are only looking, three steps with the price, the questions
 * the page does not answer, and "Opprett gratis konto" again before an
 * off-white footer. Full width at every size, one column on a phone.
 *
 * Every line is a BliMedlemPage.* translation key. Ad parameters in the
 * address are kept for the sign_up event by analytics/signupAttribution.js;
 * the event itself is sent by the sign-up (ducks/auth.duck.js), whichever of
 * the three options is used.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const BliMedlemPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();
  const t = id => intl.formatMessage({ id: `BliMedlemPage.${id}` });

  return (
    <Page
      title={t('metaTitle')}
      description={t('metaDescription')}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer className={css.footer} />}
      >
        <article className={css.root}>
          <header className={css.hero}>
            <img className={css.heroImage} src={IMAGES.hero} alt={t('heroImageAlt')} />
            <div className={css.heroText}>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="BliMedlemPage.heroHeading" />
              </h1>
              <p className={css.heroLede}>
                <FormattedMessage id="BliMedlemPage.heroLede" />
              </p>
            </div>
          </header>

          <SignupBand headingId="bli-medlem-signup" />

          <section className={css.about} aria-labelledby="bli-medlem-about">
            <img
              className={css.aboutImage}
              src={IMAGES.about}
              alt={t('aboutImageAlt')}
              loading="lazy"
            />
            <div className={css.aboutText}>
              <p className={css.eyebrow}>
                <FormattedMessage id="BliMedlemPage.aboutEyebrow" />
              </p>
              <h2 id="bli-medlem-about" className={css.sectionHeading}>
                <FormattedMessage id="BliMedlemPage.aboutHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="BliMedlemPage.aboutBody" />
              </p>
              <ul className={css.checkList}>
                {ABOUT_POINTS.map(n => (
                  <li key={n} className={css.checkItem}>
                    <IconCheckDisc className={css.checkIcon} />
                    <FormattedMessage id={`BliMedlemPage.aboutPoint${n}`} />
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className={css.why} aria-labelledby="bli-medlem-why">
            <h2 id="bli-medlem-why" className={css.eyebrowHeading}>
              <FormattedMessage id="BliMedlemPage.whyHeading" />
            </h2>
            <ul className={css.whyList}>
              {WHY_POINTS.map(({ n, Icon }) => (
                <li key={n} className={css.whyCard}>
                  <Icon className={css.whyIcon} />
                  <div className={css.whyText}>
                    <h3 className={css.whyTitle}>
                      <FormattedMessage id={`BliMedlemPage.why${n}Title`} />
                    </h3>
                    <p className={css.whyBody}>
                      <FormattedMessage id={`BliMedlemPage.why${n}Body`} />
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className={css.browse} aria-labelledby="bli-medlem-browse">
            <div className={css.browseText}>
              <p className={css.eyebrow}>
                <FormattedMessage id="BliMedlemPage.browseEyebrow" />
              </p>
              <h2 id="bli-medlem-browse" className={css.sectionHeading}>
                <FormattedMessage id="BliMedlemPage.browseHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="BliMedlemPage.browseBody" />
              </p>
            </div>
            <img
              className={css.browseImage}
              src={IMAGES.browse}
              alt={t('browseImageAlt')}
              loading="lazy"
            />
          </section>

          <section className={css.steps} aria-labelledby="bli-medlem-steps">
            <h2 id="bli-medlem-steps" className={css.eyebrowHeading}>
              <FormattedMessage id="BliMedlemPage.stepsHeading" />
            </h2>
            <div className={css.stepsRow}>
              <ol className={css.stepList}>
                {STEPS.map((n, i) => (
                  <li key={n} className={css.stepCard}>
                    <span className={css.stepNumber} aria-hidden="true">
                      {n}
                    </span>
                    <div className={css.stepText}>
                      <h3 className={css.stepTitle}>
                        <FormattedMessage id={`BliMedlemPage.step${n}Title`} />
                      </h3>
                      <p className={css.stepBody}>
                        <FormattedMessage id={`BliMedlemPage.step${n}Body`} />
                      </p>
                    </div>
                    <div className={n === 1 ? css.stepPhone : css.stepImageFrame}>
                      <img
                        className={css.stepImage}
                        src={IMAGES.steps[i]}
                        alt={t(`step${n}ImageAlt`)}
                        loading="lazy"
                      />
                    </div>
                  </li>
                ))}
              </ol>
              <div className={css.price}>
                <IconCoins className={css.priceIcon} />
                <div>
                  <h3 className={css.stepTitle}>
                    <FormattedMessage id="BliMedlemPage.priceTitle" />
                  </h3>
                  <p className={css.stepBody}>
                    <FormattedMessage id="BliMedlemPage.priceBody" />
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className={css.faq} aria-labelledby="bli-medlem-faq">
            <h2 id="bli-medlem-faq" className={css.eyebrowHeading}>
              <FormattedMessage id="BliMedlemPage.faqHeading" />
            </h2>
            <div className={css.faqList}>
              {FAQ_ITEMS.map(n => (
                <details key={n} className={css.faqItem}>
                  <summary className={css.faqQuestion}>
                    <FormattedMessage id={`BliMedlemPage.faq${n}Question`} />
                  </summary>
                  <p className={css.faqAnswer}>
                    <FormattedMessage id={`BliMedlemPage.faq${n}Answer`} />
                  </p>
                </details>
              ))}
            </div>
            <NamedLink name="FaqPage" className={css.textLink}>
              <FormattedMessage id="BliMedlemPage.faqLink" />
            </NamedLink>
          </section>

          <SignupBand headingId="bli-medlem-closing" />
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const BliMedlemPage = connect(mapStateToProps)(BliMedlemPageComponent);

export { BliMedlemPageComponent };
export default BliMedlemPage;
